import React, { useState, useEffect, useRef } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Input,
    Button,
    Badge,
    Spinner,
} from "reactstrap";
import classnames from "classnames";
import { Link } from "react-router-dom";
import io from "socket.io-client";
import SimpleBar from "simplebar-react";
import moment from "moment";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import {
    getCustomers,
    getWhatsAppStatus,
    startWhatsApp,
    getAllSuppliers
} from "../../helpers/fakebackend_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const WhatsApp = (props) => {
    document.title = "WhatsApp | CRM";

    const [qrCode, setQrCode] = useState(null);
    const [status, setStatus] = useState("loading"); // loading, disconnected, connecting, connected
    const [instancePort, setInstancePort] = useState(null);
    const [instanceRunning, setInstanceRunning] = useState(false);
    const [messages, setMessages] = useState([]);
    const [chats, setChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [curMessage, setCurMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [activeSidebarTab, setActiveSidebarTab] = useState("chats"); // chats, customers, suppliers
    const [myPhoneNumber, setMyPhoneNumber] = useState(null); // Store user's own phone number
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSendingFile, setIsSendingFile] = useState(false);

    const socketRef = useRef();
    const messageEndRef = useRef(null);
    const statusCheckInterval = useRef(null);

    const breadcrumbItems = [
        { title: "Chat", link: "#" },
        { title: "WhatsApp", link: "#" },
    ]

    useEffect(() => {
        if (typeof props.setBreadcrumbItems === "function") {
            props.setBreadcrumbItems('WhatsApp', breadcrumbItems);
        }
    }, []);

    // Initialize: Check WhatsApp status and get instance port
    useEffect(() => {
        checkWhatsAppStatus();

        // Poll status every 5 seconds
        statusCheckInterval.current = setInterval(() => {
            checkWhatsAppStatus();
        }, 5000);

        return () => {
            if (statusCheckInterval.current) {
                clearInterval(statusCheckInterval.current);
            }
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
        };
    }, []);

    // Connect to Socket when instance is running
    useEffect(() => {
        if (instanceRunning && instancePort) {
            connectToInstance(instancePort);
        }
    }, [instanceRunning, instancePort]);

    /**
     * Check WhatsApp instance status from backend
     */
    const checkWhatsAppStatus = async () => {
        try {
            const response = await getWhatsAppStatus();
            setInstancePort(response.port);
            setInstanceRunning(response.running);

            if (!response.running) {
                setStatus('disconnected');
            }
        } catch (error) {
            console.error('Failed to check WhatsApp status:', error);
            setStatus('error');
        }
    };

    /**
     * Start WhatsApp instance for this company
     */
    const startWhatsAppInstance = async () => {
        try {
            setStatus('loading');
            const response = await startWhatsApp();

            if (response.success) {
                setInstancePort(response.port);
                // Wait a bit for instance to fully start, then check status
                setTimeout(() => {
                    checkWhatsAppStatus();
                }, 2000);
            }
        } catch (error) {
            console.error('Failed to start WhatsApp:', error);
            setStatus('error');
        }
    };

    /**
     * Connect to the company's WhatsApp instance via Socket.IO
     */
    const connectToInstance = (port) => {
        if (socketRef.current) {
            socketRef.current.disconnect();
        }

        // Socket.IO connection with path option
        const whatsappHost = process.env.REACT_APP_WHATSAPP_HOST;
        let socketUrl, socketOptions;

        if (whatsappHost) {
            // Production: use HTTPS domain with path
            socketUrl = `https://${whatsappHost}`;
            socketOptions = {
                path: `/port/${port}/socket.io`
            };
        } else {
            // Development: direct connection to localhost
            socketUrl = `http://localhost:${port}`;
            socketOptions = {};
        }

        console.log(`Connecting to WhatsApp instance on port ${port}...`);
        console.log("DEBUG socketUrl:", socketUrl, "options:", socketOptions);
        setStatus("connecting");

        socketRef.current = io(socketUrl, socketOptions);

        socketRef.current.on("qr", (qr) => {
            setQrCode(qr);
            setStatus("disconnected");
        });

        socketRef.current.on("connection.update", (data) => {
            setStatus(data.status);
            if (data.status === "connected") {
                setQrCode(null);
                loadChats();
                // Get user's phone number from status endpoint
                fetchMyPhoneNumber();
            }
        });

        socketRef.current.on("message", (msg) => {
            // Update messages if the chat is currently open
            if (selectedChat && msg.key.remoteJid === selectedChat.id) {
                setMessages((prev) => [...prev, msg]);
            }
            // Always update chat list for last message preview
            loadChats();
        });

        socketRef.current.on("disconnect", () => {
            console.log("Socket disconnected");
            setStatus("disconnected");
            setQrCode(null); // Clear QR on disconnect
        });

        socketRef.current.on("connect_error", (error) => {
            console.error("Socket connection error:", error);
            setStatus("disconnected");
        });
    };

    /**
     * Fetch user's own phone number
     */
    const fetchMyPhoneNumber = async () => {
        if (!instancePort) return;
        const companyId = instancePort - 5100;
        const instanceUrl = process.env.REACT_APP_WHATSAPP_HOST
            ? `https://${process.env.REACT_APP_WHATSAPP_HOST}/instance/${companyId}`
            : `http://localhost:${instancePort}`;
        try {
            const res = await fetch(`${instanceUrl}/status`);
            const data = await res.json();
            if (data.status === 'connected' && data.phone_number) {
                setMyPhoneNumber(data.phone_number);
            }
        } catch (err) {
            console.error("Failed to fetch phone number", err);
        }
    };

    const loadChats = async () => {
        if (!instancePort) return;
        const companyId = instancePort - 5100;
        const instanceUrl = process.env.REACT_APP_WHATSAPP_HOST
            ? `https://${process.env.REACT_APP_WHATSAPP_HOST}/instance/${companyId}`
            : `http://localhost:${instancePort}`;
        try {
            const res = await fetch(`${instanceUrl}/chats`);
            const data = await res.json();
            const formattedChats = data.map(chat => {
                // Check if this is user's own number
                const chatNumber = chat.id.split('@')[0];
                const isOwnNumber = chatNumber === myPhoneNumber;

                // Try to find customer name from CRM list
                const customer = customers.find(c => {
                    const jid = formatToJid(c.phone);
                    return jid === chat.id;
                });

                // Try to find supplier name from CRM list
                const supplier = !customer ? suppliers.find(s => {
                    const jid = formatToJid(s.phone);
                    return jid === chat.id;
                }) : null;

                let displayName;
                if (isOwnNumber) {
                    // User's own number - display as "Moi (Notes)"
                    displayName = "Moi (Notes)";
                } else if (customer) {
                    displayName = `${customer.first_name} ${customer.last_name}`;
                } else if (supplier) {
                    displayName = `${supplier.name} (Fournisseur)`;
                } else {
                    displayName = chat.name || chat.id.split("@")[0];
                }

                let timestamp = chat.tc;
                // Handle cases where tc might be an object {low, high} from Long serialization
                if (typeof timestamp === 'object' && timestamp !== null) {
                    timestamp = timestamp.low || 0;
                }

                // Format date: if today show HH:mm, else show date
                let timeStr = "";
                if (timestamp) {
                    const mTime = moment(timestamp * 1000);
                    if (mTime.isSame(moment(), 'day')) {
                        timeStr = mTime.format("HH:mm");
                    } else {
                        timeStr = mTime.format("DD/MM");
                    }
                }

                return {
                    id: chat.id,
                    name: displayName,
                    lastMsg: chat.conversation || "...",
                    time: timeStr,
                    unreadCount: chat.unreadCount || 0,
                    isOwnNumber: isOwnNumber // Flag to style differently if needed
                };
            });

            // Sort chats by timestamp descending
            formattedChats.sort((a, b) => {
                const timeA = data.find(d => d.id === a.id)?.tc || 0;
                const timeB = data.find(d => d.id === b.id)?.tc || 0;
                return (timeB.low || timeB) - (timeA.low || timeA);
            });
            setChats(formattedChats);
        } catch (err) {
            console.error("Failed to load chats", err);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await getCustomers();
            setCustomers(response.data || []);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
    };

    const fetchSuppliers = async () => {
        try {
            const response = await getAllSuppliers();
            setSuppliers(response || []);
        } catch (error) {
            console.error("Error fetching suppliers:", error);
        }
    };

    const formatToJid = (phone) => {
        if (!phone) return null;
        let cleaned = phone.replace(/\D/g, "");
        if (cleaned.startsWith("0")) {
            cleaned = "212" + cleaned.substring(1);
        }
        if (!cleaned.startsWith("212") && cleaned.length === 9) {
            cleaned = "212" + cleaned;
        }
        return `${cleaned}@s.whatsapp.net`;
    };

    const handleSelectCustomer = (customer) => {
        const jid = formatToJid(customer.phone);
        if (!jid) return;

        const existingChat = chats.find(c => c.id === jid);
        if (existingChat) {
            setSelectedChat(existingChat);
        } else {
            setSelectedChat({
                id: jid,
                name: `${customer.first_name} ${customer.last_name}`,
                lastMsg: "Nouvelle discussion",
                time: ""
            });
        }
        // Switch to chats tab to show active selection if needed or stay on customers
    };

    const handleSelectSupplier = (supplier) => {
        const jid = formatToJid(supplier.phone);
        if (!jid) return;

        const existingChat = chats.find(c => c.id === jid);
        if (existingChat) {
            setSelectedChat(existingChat);
        } else {
            setSelectedChat({
                id: jid,
                name: supplier.name, // Suppliers usually have a 'name' field
                lastMsg: "Nouvelle discussion",
                time: ""
            });
        }
    };

    const loadMessages = async (jid) => {
        if (!instancePort) return;
        const companyId = instancePort - 5100;
        const instanceUrl = process.env.REACT_APP_WHATSAPP_HOST
            ? `https://${process.env.REACT_APP_WHATSAPP_HOST}/instance/${companyId}`
            : `http://localhost:${instancePort}`;
        try {
            const res = await fetch(`${instanceUrl}/messages/${encodeURIComponent(jid)}`);
            const data = await res.json();
            setMessages(data);
            scrollToBottom();

            // Mark as read in backend
            fetch(`${instanceUrl}/chats/${encodeURIComponent(jid)}/read`, { method: "POST" })
                .then(() => loadChats()); // Reload sidebar to clear badge
        } catch (err) {
            console.error("Failed to load messages");
        }
    };

    useEffect(() => {
        fetchCustomers();
        fetchSuppliers();
    }, []);

    useEffect(() => {
        if (customers.length > 0) {
            loadChats();
        }
    }, [customers]);

    useEffect(() => {
        if (selectedChat) {
            loadMessages(selectedChat.id);
        }
    }, [selectedChat, instancePort]); // Added instancePort to dependencies

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        if (messageEndRef.current) {
            messageEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    const sendMedia = async () => {
        if (!selectedFile || !selectedChat) return;
        if (!instancePort) {
            alert("WhatsApp instance not running");
            return;
        }

        setIsSendingFile(true);
        const companyId = instancePort - 5100;
        const instanceUrl = process.env.REACT_APP_WHATSAPP_HOST
            ? `https://${process.env.REACT_APP_WHATSAPP_HOST}/instance/${companyId}`
            : `http://localhost:${instancePort}`;
        const formData = new FormData();
        formData.append("number", selectedChat.id);
        formData.append("file", selectedFile);
        formData.append("caption", curMessage);

        try {
            const res = await fetch(`${instanceUrl}/send-media`, {
                method: "POST",
                body: formData,
            });

            const data = await res.json();
            if (data.success) {
                setSelectedFile(null);
                setCurMessage("");
                // Messages will be updated via socket
            }
        } catch (err) {
            console.error("Failed to send media", err);
        } finally {
            setIsSendingFile(false);
        }
    };

    const sendMessage = async () => {
        if (!curMessage.trim() || !selectedChat) return;
        if (!instancePort) {
            alert("WhatsApp instance not running");
            return;
        }

        const companyId = instancePort - 5100;
        const instanceUrl = process.env.REACT_APP_WHATSAPP_HOST
            ? `https://${process.env.REACT_APP_WHATSAPP_HOST}/instance/${companyId}`
            : `http://localhost:${instancePort}`;
        try {
            const res = await fetch(`${instanceUrl}/send-message`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    number: selectedChat.id,
                    message: curMessage,
                }),
            });

            const data = await res.json();
            if (data.success) {
                // Add message immediately to UI (optimistic update)
                const optimisticMessage = {
                    key: {
                        remoteJid: selectedChat.id,
                        fromMe: true,
                        id: Date.now().toString()
                    },
                    message: {
                        conversation: curMessage
                    },
                    messageTimestamp: Math.floor(Date.now() / 1000)
                };
                setMessages(prev => [...prev, optimisticMessage]);
                setCurMessage("");
                scrollToBottom();
            }
        } catch (err) {
            console.error("Failed to send message", err);
        }
    };

    // Render states
    if (status === "loading") {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <Spinner color="primary" />
                <span className="ms-2">Chargement...</span>
            </div>
        );
    }

    if (!instanceRunning && status === "disconnected") {
        return (
            <Row className="justify-content-center mt-5">
                <Col md={6}>
                    <Card className="text-center shadow-lg border-0" style={{ borderRadius: '20px' }}>
                        <CardBody className="p-5">
                            <div className="mb-4">
                                <i className="mdi mdi-whatsapp text-success display-3"></i>
                            </div>
                            <h4 className="mb-3">WhatsApp n'est pas démarré</h4>
                            <p className="text-muted mb-4">
                                Démarrez le service WhatsApp pour votre entreprise
                            </p>
                            <Button
                                color="success"
                                size="lg"
                                onClick={startWhatsAppInstance}
                                className="px-5"
                            >
                                <i className="mdi mdi-play me-2"></i>
                                Démarrer WhatsApp
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        );
    }

    if (status === "disconnected" && qrCode) {
        return (
            <Row className="justify-content-center mt-5">
                <Col md={6}>
                    <Card className="text-center shadow-lg border-0" style={{ borderRadius: '20px' }}>
                        <CardBody className="p-5">
                            <div className="mb-4">
                                <i className="mdi mdi-whatsapp text-success display-3"></i>
                            </div>
                            <h3 className="mb-4 fw-bold">Connectez votre WhatsApp</h3>
                            <p className="text-muted mb-4">Ouvrez WhatsApp sur votre téléphone, allez dans Paramètres {'>'} Appareils liés {'>'} Lier un appareil et scannez le code QR.</p>
                            <div className="bg-white p-3 d-inline-block rounded shadow-sm">
                                <img src={qrCode} alt="WhatsApp QR Code" style={{ width: '250px', height: '250px' }} />
                            </div>
                            <div className="mt-4">
                                <div className="spinner-grow text-success spinner-grow-sm me-2" role="status"></div>
                                <span className="text-muted">En attente de connexion...</span>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        );
    }

    if (status === "disconnected" && !qrCode) {
        return (
            <div className="text-center mt-5 p-5">
                <div className="spinner-border text-success" role="status"></div>
                <p className="mt-3">Initialisation du service WhatsApp (Baileys)...</p>
                <p className="text-muted font-size-12">Assurez-vous que le service Node.js sur le port 5001 est démarré.</p>
            </div>
        );
    }

    return (
        <div className="d-lg-flex mb-4 gap-3" style={{ height: 'calc(100vh - 180px)' }}>
            {/* Sidebar */}
            <Card className="chat-leftsidebar mb-0 shadow-sm border-0" style={{ width: '350px', borderRadius: '15px' }}>
                <div className="p-3 border-bottom bg-light" style={{ borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                            <div className="avatar-xs me-2">
                                <span className="avatar-title rounded-circle bg-success text-white">
                                    <i className="mdi mdi-whatsapp"></i>
                                </span>
                            </div>
                            <h5 className="font-size-15 mb-0">WhatsApp</h5>
                        </div>
                        <Badge color="success" className="px-2 py-1">Connecté</Badge>
                    </div>

                    <div className="d-flex gap-2 mb-3 bg-white p-1 rounded-pill shadow-sm">
                        <Button
                            color={activeSidebarTab === "chats" ? "success" : "light"}
                            className="flex-grow-1 rounded-pill py-1 border-0 font-size-13"
                            onClick={() => setActiveSidebarTab("chats")}
                        >
                            Discussions
                        </Button>
                        <Button
                            color={activeSidebarTab === "customers" ? "success" : "light"}
                            className="flex-grow-1 rounded-pill py-1 border-0 font-size-13"
                            onClick={() => setActiveSidebarTab("customers")}
                        >
                            Clients
                        </Button>
                        <Button
                            color={activeSidebarTab === "suppliers" ? "success" : "light"}
                            className="flex-grow-1 rounded-pill py-1 border-0 font-size-13"
                            onClick={() => setActiveSidebarTab("suppliers")}
                        >
                            Fourn.
                        </Button>
                    </div>

                    <div className="search-box position-relative">
                        <Input
                            type="text"
                            className="form-control border-0 bg-white rounded-pill ps-5"
                            placeholder={activeSidebarTab === "chats" ? "Rechercher..." : "Chercher un client..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <i className="mdi mdi-magnify search-icon"></i>
                    </div>
                </div>

                <SimpleBar style={{ height: "calc(100% - 160px)" }}>
                    {activeSidebarTab === "chats" ? (
                        <ul className="list-unstyled chat-list">
                            {chats.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase())).map((chat, idx) => (
                                <li key={idx} className={classnames({ active: selectedChat?.id === chat.id })}>
                                    <Link to="#" onClick={() => setSelectedChat(chat)} className="px-3 py-3 d-flex align-items-center">
                                        <div className="avatar-xs me-3 flex-shrink-0">
                                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                                {chat.name.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="flex-grow-1 overflow-hidden">
                                            <h5 className="text-truncate font-size-14 mb-1">{chat.name}</h5>
                                            <p className="text-truncate text-muted mb-0 font-size-12">{chat.lastMsg}</p>
                                        </div>
                                        <div className="d-flex flex-column align-items-end ms-2 flex-shrink-0">
                                            <div className="font-size-11 text-muted mb-1">{chat.time}</div>
                                            {chat.unreadCount > 0 && (
                                                <Badge color="success" pill className="font-size-10">
                                                    {chat.unreadCount}
                                                </Badge>
                                            )}
                                        </div>
                                    </Link>
                                </li>
                            ))}
                            {chats.length === 0 && (
                                <div className="text-center py-5 text-muted font-size-13">
                                    Aucune discussion active
                                </div>
                            )}
                        </ul>
                    ) : activeSidebarTab === "customers" ? (
                        <ul className="list-unstyled chat-list">
                            {customers.filter(c =>
                                (`${c.first_name} ${c.last_name}`).toLowerCase().includes(searchQuery.toLowerCase()) ||
                                (c.phone && c.phone.includes(searchQuery))
                            ).map((customer, idx) => {
                                const jid = formatToJid(customer.phone);
                                const isSelected = selectedChat?.id === jid;
                                return (
                                    <li key={idx} className={classnames({ active: isSelected })}>
                                        <Link to="#" onClick={() => handleSelectCustomer(customer)} className="px-3 py-3 d-flex align-items-center">
                                            <div className="avatar-xs me-3 flex-shrink-0">
                                                <span className="avatar-title rounded-circle bg-info-subtle text-info">
                                                    {customer.first_name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-grow-1 overflow-hidden">
                                                <h5 className="text-truncate font-size-14 mb-1">{customer.first_name} {customer.last_name}</h5>
                                                <p className="text-truncate text-muted mb-0 font-size-12">
                                                    <i className="mdi mdi-phone-outline me-1"></i> {customer.phone}
                                                </p>
                                            </div>
                                            <div className="font-size-18 text-success">
                                                <i className="mdi mdi-whatsapp"></i>
                                            </div>
                                        </Link>
                                    </li>
                                )
                            })}
                            {customers.length === 0 && (
                                <div className="text-center py-5 text-muted font-size-13">
                                    Aucun client trouvé
                                </div>
                            )}
                        </ul>
                    ) : activeSidebarTab === "suppliers" ? (
                        <ul className="list-unstyled chat-list">
                            {suppliers.filter(s =>
                                (s.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                                (s.phone && s.phone.includes(searchQuery))
                            ).map((supplier, idx) => {
                                const jid = formatToJid(supplier.phone);
                                const isSelected = selectedChat?.id === jid;
                                return (
                                    <li key={idx} className={classnames({ active: isSelected })}>
                                        <Link to="#" onClick={() => handleSelectSupplier(supplier)} className="px-3 py-3 d-flex align-items-center">
                                            <div className="avatar-xs me-3 flex-shrink-0">
                                                <span className="avatar-title rounded-circle bg-warning-subtle text-warning">
                                                    {supplier.name ? supplier.name.charAt(0).toUpperCase() : "S"}
                                                </span>
                                            </div>
                                            <div className="flex-grow-1 overflow-hidden">
                                                <h5 className="text-truncate font-size-14 mb-1">{supplier.name}</h5>
                                                <p className="text-truncate text-muted mb-0 font-size-12">
                                                    <i className="mdi mdi-phone-outline me-1"></i> {supplier.phone}
                                                </p>
                                            </div>
                                            <div className="font-size-18 text-success">
                                                <i className="mdi mdi-whatsapp"></i>
                                            </div>
                                        </Link>
                                    </li>
                                )
                            })}
                            {suppliers.length === 0 && (
                                <div className="text-center py-5 text-muted font-size-13">
                                    Aucun fournisseur trouvé
                                </div>
                            )}
                        </ul>
                    ) : null}
                </SimpleBar>
            </Card >

            {/* Chat Box */}
            < Card className="w-100 mb-0 shadow-sm border-0" style={{ borderRadius: '15px' }}>
                {
                    selectedChat ? (
                        <div className="d-flex flex-column h-100" >
                            <div className="p-3 border-bottom bg-light" style={{ borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}>
                                <Row className="align-items-center">
                                    <Col>
                                        <div className="d-flex align-items-center">
                                            <div className="avatar-xs me-3 flex-shrink-0">
                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary">
                                                    {selectedChat.name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-grow-1">
                                                <h5 className="font-size-16 mb-0">{selectedChat.name}</h5>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            <SimpleBar className="flex-grow-1 p-4 bg-light-subtle" style={{ height: '100px', backgroundImage: 'url(https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png)', backgroundRepeat: 'repeat' }}>
                                <ul className="list-unstyled mb-0">
                                    {messages.map((msg, idx) => {
                                        const isMe = msg.key.fromMe;
                                        const messageType = Object.keys(msg.message || {})[0];
                                        const content = msg.message?.[messageType];

                                        const text = msg.message?.conversation ||
                                            msg.message?.extendedTextMessage?.text ||
                                            content?.caption || "";

                                        const isImage = messageType === 'imageMessage';
                                        const isDocument = messageType === 'documentMessage';
                                        const isVideo = messageType === 'videoMessage';
                                        const isAudio = messageType === 'audioMessage';

                                        const mediaUrl = (isImage || isDocument || isVideo || isAudio)
                                            ? (process.env.REACT_APP_WHATSAPP_HOST ? `https://${process.env.REACT_APP_WHATSAPP_HOST}/port/${instancePort}/media/${msg.key.id}?jid=${encodeURIComponent(selectedChat.id)}` : `http://localhost:${instancePort}/media/${msg.key.id}?jid=${encodeURIComponent(selectedChat.id)}`)
                                            : null;

                                        return (
                                            <li key={idx} className={classnames("mb-3 d-flex", { "justify-content-end": isMe })}>
                                                <div className={classnames("p-3 shadow-sm", {
                                                    "bg-success text-white": isMe,
                                                    "bg-white border": !isMe
                                                })} style={{
                                                    maxWidth: '70%',
                                                    borderRadius: isMe ? '15px 15px 0px 15px' : '15px 15px 15px 0px',
                                                    minWidth: '100px'
                                                }}>
                                                    {isImage && (
                                                        <div className="mb-2">
                                                            <img
                                                                src={mediaUrl}
                                                                alt="WhatsApp image"
                                                                className="img-fluid rounded"
                                                                style={{ maxHeight: '300px', cursor: 'pointer' }}
                                                                onClick={() => window.open(mediaUrl, '_blank')}
                                                            />
                                                        </div>
                                                    )}
                                                    {isDocument && (
                                                        <div className={classnames("d-flex align-items-center mb-2 p-2 rounded", isMe ? "bg-success-subtle text-dark" : "bg-light")}>
                                                            <i className="mdi mdi-file-document-outline font-size-24 me-2 text-primary"></i>
                                                            <div className="overflow-hidden">
                                                                <p className="mb-0 text-truncate font-size-13 fw-bold">{content.fileName || "Document"}</p>
                                                                <a href={mediaUrl} target="_blank" rel="noreferrer" className="font-size-11 text-primary">Télécharger</a>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {isVideo && (
                                                        <div className="mb-2 text-center bg-black rounded p-1">
                                                            <i className="mdi mdi-play-circle-outline display-4 text-white"></i>
                                                            <p className="text-white font-size-11 mb-0">Vidéo (cliquez pour voir)</p>
                                                            <a href={mediaUrl} target="_blank" rel="noreferrer" className="stretched-link"></a>
                                                        </div>
                                                    )}
                                                    {text && <p className="mb-0 font-size-14">{text}</p>}
                                                    {!isImage && !isDocument && !isVideo && !isAudio && !text && (
                                                        <p className="mb-0 font-size-14 text-muted font-italic">Message non supporté</p>
                                                    )}
                                                    <div className={classnames("font-size-10 mt-1 text-end", { "text-white-50": isMe, "text-muted": !isMe })}>
                                                        {moment(msg.messageTimestamp * 1000).format("HH:mm")}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                    <div ref={messageEndRef} />
                                </ul>
                            </SimpleBar>

                            <div className="p-3 border-top">
                                {selectedFile && (
                                    <div className="p-2 mb-2 bg-light rounded d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <i className="mdi mdi-file-outline font-size-18 me-2 text-primary"></i>
                                            <span className="font-size-13 text-truncate" style={{ maxWidth: '200px' }}>{selectedFile.name}</span>
                                        </div>
                                        <Button close onClick={() => setSelectedFile(null)} size="sm" />
                                    </div>
                                )}
                                <Row className="g-2">
                                    <Col className="col-auto">
                                        <input
                                            type="file"
                                            id="chat-file-input"
                                            className="d-none"
                                            onChange={(e) => setSelectedFile(e.target.files[0])}
                                        />
                                        <Button
                                            color="light"
                                            className="rounded-circle"
                                            onClick={() => document.getElementById('chat-file-input').click()}
                                        >
                                            <i className="mdi mdi-paperclip font-size-18"></i>
                                        </Button>
                                    </Col>
                                    <Col className="col">
                                        <Input
                                            type="text"
                                            className="form-control rounded-pill border-0 bg-light px-4"
                                            placeholder={selectedFile ? "Ajouter une légende..." : "Écrivez un message..."}
                                            value={curMessage}
                                            onChange={(e) => setCurMessage(e.target.value)}
                                            onKeyPress={(e) => e.key === "Enter" && (selectedFile ? sendMedia() : sendMessage())}
                                        />
                                    </Col>
                                    <Col className="col-auto">
                                        <Button
                                            color="success"
                                            className="rounded-circle btn-lg"
                                            onClick={selectedFile ? sendMedia : sendMessage}
                                            disabled={isSendingFile}
                                        >
                                            {isSendingFile ? <Spinner size="sm" /> : <i className="mdi mdi-send"></i>}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    ) : (
                        <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted bg-light-subtle p-5" style={{ borderRadius: '15px' }}>
                            <div className="avatar-lg mb-4">
                                <span className="avatar-title rounded-circle bg-success text-white display-4 shadow">
                                    <i className="mdi mdi-whatsapp"></i>
                                </span>
                            </div>
                            <h4 className="fw-bold">WhatsApp pour CRM</h4>
                            <p className="text-center opacity-75">Gérez vos conversations WhatsApp directement depuis votre interface CRM.<br />Les messages sont synchronisés en temps réel.</p>
                        </div>
                    )}
            </Card >
        </div >
    );
};

const mapDispatchToProps = {
    setBreadcrumbItems
};

export default connect(null, mapDispatchToProps)(WhatsApp);
